import { useCallback, useEffect, useState } from "react";
import { theme, card } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { PortalLink } from "../components/PortalLink";
import { gbpMo, HARDENING_BENCHMARK_PENCE } from "../lib/pricing";

// "Recommended for your assets" on the CIS Hardening page: the benchmarks the
// asset register calls for (GET /hardening/recommendations, rules in
// backend/src/lib/hardeningRecommendations.ts) with the reason each applies.
// Subscribing uses the existing subscription route, exactly as My Services does;
// billing is unchanged (HARDENING_BENCHMARK_PENCE per benchmark, from lib/pricing.ts).

export interface HardeningRecommendation {
  benchmarkId: string;
  name: string;
  category: string;
  version: string;
  reason: string;
  assetCount: number;
  assetIds: string[];
  subscribed: boolean;
  confidence: "confirmed" | "unconfirmed";
}

interface Props {
  /** The session holds controls:write (the API enforces it too). */
  canSubscribe: boolean;
  /** Called after a subscription is added so the page reloads its benchmark list. */
  onSubscribed: () => Promise<void> | void;
  /** Opens a subscribed benchmark; absent when the page has nowhere to open it. */
  onOpen?: (benchmarkId: string) => void;
}

const smallBtn = (primary: boolean): React.CSSProperties => ({
  fontSize: 12, fontWeight: 600, padding: "5px 12px", borderRadius: 3, cursor: "pointer", fontFamily: theme.font, whiteSpace: "nowrap",
  border: primary ? "none" : `1px solid ${theme.color.border}`, background: primary ? theme.color.accent : "transparent", color: primary ? "#fff" : theme.color.text,
});

export function HardeningRecommendations({ canSubscribe, onSubscribed, onOpen }: Props) {
  const [data, setData] = useState<{ recommendations: HardeningRecommendation[]; assetCount: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showUnconfirmed, setShowUnconfirmed] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api.get<{ recommendations: HardeningRecommendation[]; assetCount: number }>("/hardening/recommendations")); setError(null); }
    catch (e) { setError(apiErrorMessage(e, "Could not load the recommendations")); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const subscribe = async (r: HardeningRecommendation) => {
    if (!window.confirm(`Subscribe to ${r.name} (${r.version}) at ${gbpMo(HARDENING_BENCHMARK_PENCE)}? It can be removed again on My Services.`)) return;
    setBusy(r.benchmarkId); setError(null);
    try { await api.post("/hardening/subscriptions", { benchmarkId: r.benchmarkId }); await onSubscribed(); await load(); }
    catch (e) { setError(apiErrorMessage(e, "Could not subscribe to the benchmark")); }
    finally { setBusy(null); }
  };

  if (!data && !error) return null;
  const recs = data?.recommendations ?? [];
  const confirmed = recs.filter((r) => r.confidence === "confirmed");
  const unconfirmed = recs.filter((r) => r.confidence === "unconfirmed");
  const toAdd = confirmed.filter((r) => !r.subscribed).length;

  const row = (r: HardeningRecommendation) => (
    <div key={r.benchmarkId} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", borderTop: `1px solid ${theme.color.border}` }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{r.name} <span style={{ fontSize: 11, fontWeight: 400, color: theme.color.faint }}>{r.version}</span></div>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 1 }}>{r.reason}</div>
      </div>
      {r.subscribed
        ? <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 3, background: theme.color.successBg, color: theme.color.successText, textTransform: "uppercase", letterSpacing: "0.04em" }}>Subscribed</span>
            {onOpen && <button type="button" onClick={() => onOpen(r.benchmarkId)} style={smallBtn(false)}>Open</button>}
          </span>
        : canSubscribe
          ? <button type="button" disabled={busy !== null} onClick={() => subscribe(r)} style={{ ...smallBtn(true), opacity: busy ? 0.6 : 1 }}>{busy === r.benchmarkId ? "Subscribing…" : "Subscribe"}</button>
          : <span style={{ fontSize: 11, color: theme.color.faint }}>Not subscribed</span>}
    </div>
  );

  return (
    <div style={{ ...card, marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 4 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>Recommended for your assets</div>
        {data && data.assetCount > 0 && <div style={{ fontSize: 11, color: theme.color.faint }}>{data.assetCount} active asset{data.assetCount === 1 ? "" : "s"} in the register{toAdd ? ` · ${toAdd} benchmark${toAdd === 1 ? "" : "s"} to add` : ""}</div>}
      </div>
      <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.5, marginBottom: confirmed.length ? 8 : 0 }}>
        What the <PortalLink to="/resilience/assets" style={{ color: theme.color.accentText }}>asset register</PortalLink> says the organisation runs, matched to the CIS benchmarks that harden it. Each benchmark is {gbpMo(HARDENING_BENCHMARK_PENCE)}, as on My Services.
      </div>
      {error && <div style={{ fontSize: 12, color: theme.color.dangerText, marginTop: 8 }}>{error}</div>}
      {data && data.assetCount === 0 && (
        <div style={{ fontSize: 12, color: theme.color.muted, marginTop: 8, lineHeight: 1.5 }}>
          The asset register is empty. Connected sources (Intune, Microsoft 365, AWS, Google Cloud, firewalls) and members populate it; recommendations appear here once it has something to match.
        </div>
      )}
      {data && data.assetCount > 0 && recs.length === 0 && (
        <div style={{ fontSize: 12, color: theme.color.muted, marginTop: 8 }}>Nothing in the asset register matches a CIS benchmark yet. Record the operating system of servers and endpoints to get matches.</div>
      )}
      {confirmed.map(row)}
      {unconfirmed.length > 0 && (
        <div style={{ marginTop: confirmed.length ? 10 : 8 }}>
          <button type="button" onClick={() => setShowUnconfirmed((v) => !v)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 12, color: theme.color.accentText, fontFamily: theme.font }}>
            {showUnconfirmed ? "Hide" : "Show"} {unconfirmed.length} that may also apply
          </button>
          <span style={{ fontSize: 11, color: theme.color.faint, marginLeft: 8 }}>Servers and endpoints whose operating system is not recorded; confirm it in the asset register.</span>
          {showUnconfirmed && unconfirmed.map(row)}
        </div>
      )}
    </div>
  );
}
